import { Component } from 'preact';
import AppHeader from './AppHeader';
import { BackButton } from './ui/navButtons';
import { TabBar } from './ui/TabBar';
import AmbientParticles from './AmbientParticles';
import StoreTorchFX from './StoreTorchFX';
import {
  GOLD, GOLD_TEXT, TEXT_PRIMARY, TEXT_BODY, TEXT_MUTED, ACCENT_GOLD,
  BEVELED_BTN, INPUT_STYLE, BG_ATMOSPHERE, VIGNETTE, PANEL_BG,
  FourCorners, OrnamentalDivider, CONTENT_BG_DEFAULT,
} from '../lib/medievalTheme';
import { playUI, UI } from '../utils/arena/uiSounds';
import DeckImportModal from './DeckImportModal';
import CreateDeckModal from './CreateDeckModal';
import DeckTagMenu, { ManageTagsModal, TAG_GLYPH } from './DeckTagMenu';
import DeckAdvancedFilters from './DeckAdvancedFilters';
import DeckLibrary from './DeckLibrary';
import { isPhoneLayout } from '../utils/platform';
import DeckTile, { TXT, DeckTileText, DeckModePill } from './DeckTile';
import DeckShowcasePicker from './DeckShowcasePicker';
import { pushDismiss, removeDismiss } from '../utils/escStack';
import TutorialOverlay from './TutorialOverlay';
import { shouldAutoPlay, markTutorialSeen, hydrateTutorialState } from '../utils/arena/tutorialState';
import { timeAgo } from '../utils/arena/timeFormat';
import { getMissingCards } from '../utils/sorcery/deckRules';
import { buildOwnedMap } from '../utils/arena/collectionUtils';
import { TAB_ACTIVE_TEXT, TAB_INACTIVE, TAB_BAR_STYLE } from '../lib/medievalTheme';
import { t, k } from '../localization/t';

const GALLERY_TUTORIAL_KEY = 'deck-gallery-v1';

const GALLERY_TUTORIAL_STEPS = [
  {
    key: 'my-decks',
    title: k('My Decks'),
    body: k('Every deck you\'ve built lives here. Click one to edit it, duplicate it, or take it into a match.'),
    selector: '[data-tutorial="gallery-tab-my-decks"]',
  },
  {
    key: 'library',
    title: k('Public'),
    body: k('Switch tabs to browse decks other players have published. Open one to study it or copy it into your own list.'),
    selector: '[data-tutorial="gallery-tab-library"]',
  },
  {
    key: 'import',
    title: k('Import from Curiosa'),
    body: k('Paste a Curiosa URL or a raw deck list and Valkenhall will build the deck for you.'),
    selector: '[data-tutorial="deck-import"]',
  },
  {
    key: 'search',
    title: k('Search, Filter, and Sort'),
    body: k('Search by name, filter by avatar or format (Constructed, Peasant, or Sandbox), and sort by Latest, Name, or Oldest.'),
    selector: '[data-tutorial="deck-search"]',
  },
  {
    key: 'forge',
    title: k('Forge a New Deck'),
    body: k('Click the Forge New Deck tile to start a fresh build from scratch.'),
    selector: '[data-tutorial="forge-new-deck"]',
  },
];

// Stable empty value for the library capsule's disabled element filter,
// so its activeCount never sees a fresh Set per render.
const EMPTY_SET = new Set();

export default class DeckGallery extends Component {
  constructor(props) {
    super(props);
    this.state = {
      // Phone: chrome (title bar + controls) hides while scrolling down
      // and returns on scroll-up, so the grid gets the whole screen.
      chromeHidden: false,
      searchQuery: '',
      myDeckSort: 'latest', // 'latest' | 'name' | 'oldest'
      myDeckAvatar: null,
      // Format filter for the My Decks tab. 'all' shows everything,
      // the other values mirror the gameMode tag on each saved deck.
      myDeckType: 'all', // 'all' | 'constructed' | 'peasant' | 'sandbox'
      // Tag filter. 'all' shows everything, '__none__' shows only
      // untagged decks, any other value matches one of a deck's tags.
      myDeckTag: 'all',
      // Element filter: the deck must include every selected element.
      myDeckElements: new Set(),
      showCreate: false,
      // The open tag menu: { deckId, anchor } (anchor = the tag
      // button's viewport rect), plus the manage-tags modal.
      tagMenu: null,
      showManageTags: false,
      confirmDeleteId: null,
      // Collection-wide edit mode: deck names become editable and every
      // tile's cover + fanned cards become art pick targets.
      editMode: false,
      // deckId -> { cover, cards } draft, so a tile previews a pick
      // immediately while the save round-trips.
      showcaseDrafts: {},
      // The slot whose picker is open: { deckId, slot, anchor }.
      activeSlot: null,
      showImport: false,
      galleryTab: props.initialTab || 'my-decks', // 'my-decks' | 'library'
      // Library filter state. Owned by DeckGallery so the toolbar
      // controls render in this component's tree, not via a ref.
      libSearch: '',
      libSort: 'latest',
      libTab: 'all',
      libAvatar: null,
      libType: 'all',
      showGalleryTutorial: false,
    };
    this._libSearchTimer = null;
  }

  componentDidMount() {
    const profileId = this.props.profile?.id;
    if (!profileId) return;
    hydrateTutorialState().then(() => {
      if (this._unmounted) return;
      if (!shouldAutoPlay(profileId, GALLERY_TUTORIAL_KEY)) return;
      this._tutorialTimer = setTimeout(() => {
        if (!this._unmounted) this.setState({ showGalleryTutorial: true });
      }, 400);
    });
  }

  componentDidUpdate(prevProps) {
    // A deep-link can arrive while the gallery is already mounted.
    const tab = this.props.initialTab;
    if (tab && tab !== prevProps.initialTab && tab !== this.state.galleryTab) {
      this.setState({ galleryTab: tab });
    }
  }

  componentWillUnmount() {
    this._unmounted = true;
    if (this._tutorialTimer) clearTimeout(this._tutorialTimer);
    removeDismiss(this._showcaseEscId);
  }

  // Phone chrome auto-hide. Direction-based with small hysteresis so
  // tiny scroll jitter doesn't flicker the header; always shown near
  // the top of the list.
  handleBodyScroll = (e) => {
    if (!isPhoneLayout()) return;
    const st = e.currentTarget.scrollTop;
    const dy = st - (this._lastScrollTop || 0);
    this._lastScrollTop = st;
    if (st < 8) {
      if (this.state.chromeHidden) this.setState({ chromeHidden: false });
    } else if (st > 60 && dy > 6 && !this.state.chromeHidden) {
      this.setState({ chromeHidden: true });
    } else if (dy < -6 && this.state.chromeHidden) {
      this.setState({ chromeHidden: false });
    }
  };

  toggleEditMode = () => {
    if (this.state.editMode) {
      this.exitEditMode();
      return;
    }
    this.setState({ editMode: true, activeSlot: null, showcaseDrafts: {} });
    this._showcaseEscId = pushDismiss(this.exitEditMode);
  };

  exitEditMode = () => {
    removeDismiss(this._showcaseEscId);
    this._showcaseEscId = null;
    this.setState({ editMode: false, activeSlot: null, showcaseDrafts: {} });
  };

  showcaseFor(deck) {
    const draft = this.state.showcaseDrafts[deck.id];
    if (draft) return draft;
    const showcase = deck.showcase || {};
    const picks = Array.isArray(showcase.cards) ? showcase.cards : [];
    return { cover: showcase.cover || null, cards: [picks[0] || null, picks[1] || null, picks[2] || null] };
  }

  // `cardId` null restores the slot's default pick.
  pickShowcaseCard = (cardId) => {
    const { activeSlot } = this.state;
    const deck = (this.props.savedDecks || []).find((d) => d.id === activeSlot?.deckId);
    if (!deck) return;
    const base = this.showcaseFor(deck);
    const draft = activeSlot.slot === 'cover'
      ? { ...base, cover: cardId }
      : { ...base, cards: base.cards.map((c, i) => (i === activeSlot.slot ? cardId : c)) };
    this.setState((s) => ({ showcaseDrafts: { ...s.showcaseDrafts, [deck.id]: draft }, activeSlot: null }));
    playUI(UI.CONFIRM);
    this.props.onSaveShowcase?.(deck.id, draft);
  };

  commitDeckName = (deck, value) => {
    const name = (value || '').trim();
    if (!name || name === deck.name) return;
    this.props.onRenameDeck?.(deck.id, name);
  };

  handleGalleryTutorialDismiss = () => {
    const profileId = this.props.profile?.id;
    if (profileId) markTutorialSeen(profileId, GALLERY_TUTORIAL_KEY);
    this.setState({ showGalleryTutorial: false });
  };

  getFilteredDecks() {
    const { savedDecks } = this.props;
    const { searchQuery, myDeckSort, myDeckAvatar, myDeckType, myDeckTag, myDeckElements } = this.state;
    if (!savedDecks) return [];
    let decks = [...savedDecks];
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      decks = decks.filter((d) => d.name.toLowerCase().includes(q));
    }
    if (myDeckAvatar) {
      decks = decks.filter((d) => (d.cards || []).some((c) => c.cardId === myDeckAvatar));
    }
    if (myDeckType && myDeckType !== 'all') {
      decks = decks.filter((d) => (d.gameMode || 'constructed') === myDeckType);
    }
    if (myDeckTag && myDeckTag !== 'all') {
      decks = decks.filter((d) => (myDeckTag === '__none__'
        ? (d.tags || []).length === 0
        : (d.tags || []).includes(myDeckTag)));
    }
    if (myDeckElements && myDeckElements.size > 0) {
      const elementIndex = this.getCardElementIndex();
      decks = decks.filter((d) => {
        const deckElements = new Set();
        for (const c of d.cards || []) {
          const els = elementIndex.get(c.cardId);
          if (els) for (const e of els) deckElements.add(e);
        }
        return [...myDeckElements].every((e) => deckElements.has(e));
      });
    }
    if (myDeckSort === 'name') decks.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    else if (myDeckSort === 'oldest') decks.sort((a, b) => new Date(a.savedAt || 0) - new Date(b.savedAt || 0));
    // Favorites always float to the top, keeping the chosen sort within
    // each group (stable sort preserves the order set above).
    decks.sort((a, b) => (b.favorite ? 1 : 0) - (a.favorite ? 1 : 0));
    return decks;
  }

  getAvatarOptions() {
    const cards = this.props.sorceryCards;
    if (!Array.isArray(cards)) return [];
    return cards
      .filter((c) => c.type === 'Avatar')
      .map((c) => ({ value: c.unique_id, label: c.name, imageUrl: c.printings?.[0]?.image_url || null }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }

  // cardId -> element names, memoized by catalog reference. Saved decks
  // only carry card ids, so the element filter resolves them here.
  getCardElementIndex() {
    const cards = this.props.sorceryCards;
    if (this._elementIndexCards === cards && this._elementIndex) return this._elementIndex;
    const index = new Map();
    for (const c of cards || []) {
      const els = (c.elements || []).map((e) => e?.name).filter(Boolean);
      if (els.length > 0) index.set(c.unique_id, els);
    }
    this._elementIndexCards = cards;
    this._elementIndex = index;
    return index;
  }

  // Every tag in the collection with its deck count, name-sorted.
  // Tags exist only on decks, so this is the whole tag registry.
  getTagOptions() {
    const counts = new Map();
    for (const d of this.props.savedDecks || []) {
      for (const t of d.tags || []) counts.set(t, (counts.get(t) || 0) + 1);
    }
    return [...counts.entries()]
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  render() {
    const { onBack, onCreateDeck, onOpenDeck, onOpenLibraryDeck, onDeleteDeck, onDuplicateDeck, onToggleShare, onToggleFavorite, sorceryCards, onToggleMailbox, mailboxUnreadCount, mailboxDropdown, onToggleFriends, friendListData } = this.props;
    const { searchQuery, confirmDeleteId } = this.state;
    const filteredDecks = this.getFilteredDecks();

    return (
      <div className="fixed inset-0 z-50 flex flex-col" style={{ background: 'url("/deck-builder-bg.webp") center no-repeat, #08080a', backgroundSize: '100% 100%' }}>
        <div className="absolute inset-0 pointer-events-none" style={{ background: VIGNETTE }} />
        <StoreTorchFX />
        <AmbientParticles preset="deckbuilder" />

        {/* Phone: the chrome overlays the grid and slides away on
            scroll-down via transform. It must NOT live in the layout
            flow: collapsing an in-flow header resizes the scroller,
            the browser clamps scrollTop, and the hide/show oscillates
            (the stutter this replaces). Desktop: display contents,
            zero layout effect. */}
        <div
          className={isPhoneLayout() ? 'absolute inset-x-0 top-0 z-30' : 'contents'}
          style={isPhoneLayout() ? {
            background: '#0b0906',
            borderBottom: `1px solid ${GOLD} 0.15)`,
            transform: this.state.chromeHidden ? 'translateY(-105%)' : 'translateY(0)',
            transition: 'transform 280ms ease',
            paddingTop: 'env(safe-area-inset-top, 0px)',
          } : undefined}
        >
        {isPhoneLayout() ? (
          <div
            className="relative z-20 shrink-0 flex items-center gap-3 pt-2 pb-1"
            style={{
              paddingLeft: 'max(0.75rem, env(safe-area-inset-left, 0px))',
              paddingRight: 'max(0.75rem, env(safe-area-inset-right, 0px))',
            }}
          >
            <button
              type="button"
              aria-label={t('Back')}
              className="flex items-center justify-center shrink-0 transition-transform duration-100 active:scale-[0.94]"
              style={{
                width: '2.75rem',
                height: '2.75rem',
                borderRadius: 'var(--radius-full)',
                background: 'linear-gradient(180deg, rgba(20,16,10,0.85) 0%, rgba(10,8,5,0.9) 100%)',
                border: '1px solid rgba(180, 140, 60, 0.28)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.5)',
              }}
              onClick={onBack}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '1.375rem', height: '1.375rem', color: TEXT_MUTED }}>
                <path d="m15 18-6-6 6-6" />
              </svg>
            </button>
            <h1 className="arena-heading text-lg font-bold" style={{ color: TEXT_PRIMARY, textShadow: '0 2px 4px rgba(0,0,0,0.5)' }}>{t('Deck Collection')}</h1>
          </div>
        ) : (
          <AppHeader
            profile={this.props.profile}
            onToggleMailbox={onToggleMailbox}
            mailboxUnreadCount={mailboxUnreadCount}
            mailboxDropdown={mailboxDropdown}
            onToggleFriends={onToggleFriends}
            friendListData={friendListData}
          >
            <BackButton title={t('Back to Hub')} onClick={onBack} />
            <h1 className="arena-heading text-lg font-bold" style={{ color: TEXT_PRIMARY, textShadow: '0 2px 4px rgba(0,0,0,0.5)' }}>{t('Deck Collection')}</h1>
          </AppHeader>
        )}

        {/* Gallery tabs + search bar */}
        <div
          className={`relative z-20 shrink-0 flex items-center gap-3 ${isPhoneLayout() ? 'pt-1 pb-2' : 'px-6 pt-3 pb-3'}`}
          style={{
            overflow: 'visible',
            ...(isPhoneLayout() ? {
              paddingLeft: 'max(0.75rem, env(safe-area-inset-left, 0px))',
              paddingRight: 'max(0.75rem, env(safe-area-inset-right, 0px))',
            } : {}),
          }}
        >
          {/* Tabs */}
          <TabBar
            className="inline-flex items-center shrink-0"
            style={{ ...TAB_BAR_STYLE, background: '#0e0b08' }}
            activeIndex={this.state.galleryTab === 'library' ? 1 : 0}
          >
            <button
              type="button"
              data-tab-item
              data-tutorial="gallery-tab-my-decks"
              className="px-4 py-2 text-xs font-semibold"
              style={this.state.galleryTab === 'my-decks' ? TAB_ACTIVE_TEXT : TAB_INACTIVE}
              onClick={() => this.setState({ galleryTab: 'my-decks' })}
            >
              {t('My Decks')}
            </button>
            <button
              type="button"
              data-tab-item
              data-tutorial="gallery-tab-library"
              className="px-4 py-2 text-xs font-semibold"
              style={this.state.galleryTab === 'library' ? TAB_ACTIVE_TEXT : TAB_INACTIVE}
              onClick={() => this.setState({ galleryTab: 'library' }, () => this.forceUpdate())}
            >
              {t('Public')}
            </button>
          </TabBar>
          {/* Import. Always visible: imports land in My Decks whichever
              tab is open, and the affordance vanishing on tab switch
              read as a bug. */}
          {(
            <button
              type="button"
              data-tutorial="deck-import"
              className="flex items-center justify-center shrink-0 rounded-lg transition-all duration-200 hover:scale-[1.05]"
              style={{
                background: '#0e0b08',
                border: `1px solid ${GOLD} 0.18)`,
                color: TEXT_MUTED,
                width: isPhoneLayout() ? '2.75rem' : '2.375cqw',
                height: isPhoneLayout() ? '2.75rem' : '2.375cqw',
              }}
              title={t('Import deck')}
              onClick={() => this.setState({ showImport: true })}
            >
              <svg style={{ width: isPhoneLayout() ? '1.25rem' : '0.9375cqw', height: isPhoneLayout() ? '1.25rem' : '0.9375cqw' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
            </button>
          )}

          {/* My Decks search bar with avatar + sort (desktop: filters
              embedded in the capsule; phone consolidates them into the
              funnel panel below). */}
          {/* My Decks search + advanced filters. One capsule (search
              input + sliders button) opening a gold popover panel,
              same component family as the deck builder's CardFilterBar.
              The individual toolbar dropdowns this replaces had grown
              too cramped. */}
          {!isPhoneLayout() && this.state.galleryTab === 'my-decks' && (
            <DeckAdvancedFilters
              searchQuery={searchQuery}
              onSearchChange={(v) => this.setState({ searchQuery: v })}
              sort={this.state.myDeckSort}
              onSortChange={(v) => this.setState({ myDeckSort: v })}
              type={this.state.myDeckType}
              onTypeChange={(v) => this.setState({ myDeckType: v })}
              tag={this.state.myDeckTag}
              onTagChange={(v) => this.setState({ myDeckTag: v })}
              elements={this.state.myDeckElements}
              onElementsChange={(v) => this.setState({ myDeckElements: v })}
              avatar={this.state.myDeckAvatar}
              onAvatarChange={(v) => this.setState({ myDeckAvatar: v })}
              avatarOptions={this.getAvatarOptions()}
              tagOptions={this.getTagOptions()}
              onManageTags={() => this.setState({ showManageTags: true })}
            />
          )}

          {/* Phone: plain search input + one funnel button. All filters
              (sort / type / avatar, and the library's Show tabs) live in
              a consolidated dropdown panel - the desktop capsule with
              embedded controls does not fit a phone-width row. */}
          {isPhoneLayout() && (() => {
            const isLib = this.state.galleryTab === 'library';
            const reloadLib = () => this.deckLibraryRef?.loadDecks();
            const pill = (active, label, onClick) => (
              <button
                key={label}
                type="button"
                className="px-3 rounded-full text-xs font-semibold transition-all"
                style={{
                  minHeight: '2rem',
                  background: active ? `${GOLD} 0.16)` : 'rgba(255,255,255,0.04)',
                  border: `1px solid ${GOLD} ${active ? '0.55)' : '0.16)'}`,
                  color: active ? ACCENT_GOLD : TEXT_BODY,
                }}
                onClick={onClick}
              >
                {label}
              </button>
            );
            const anyFilter = isLib
              ? (this.state.libAvatar || this.state.libSort !== 'latest' || this.state.libTab !== 'all')
              : (this.state.myDeckAvatar || this.state.myDeckSort !== 'latest' || this.state.myDeckType !== 'all' || this.state.myDeckTag !== 'all' || this.state.myDeckElements.size > 0);
            const avatarOptions = this.getAvatarOptions();
            return (
              <>
                <div className="relative shrink-0">
                  <button
                    type="button"
                    aria-label={t('Filters')}
                    className="relative flex items-center justify-center rounded-lg transition-all active:scale-[0.95]"
                    style={{
                      width: '2.75rem',
                      height: '2.75rem',
                      background: '#0e0b08',
                      border: `1px solid ${GOLD} ${this.state.showMobileFilters || anyFilter ? '0.5)' : '0.18)'}`,
                      color: anyFilter ? ACCENT_GOLD : TEXT_MUTED,
                    }}
                    onClick={() => this.setState((st) => ({ showMobileFilters: !st.showMobileFilters }))}
                  >
                    <svg style={{ width: '1.25rem', height: '1.25rem' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
                    </svg>
                    {anyFilter ? (
                      <span className="absolute rounded-full" style={{ top: '0.375rem', right: '0.375rem', width: '0.5rem', height: '0.5rem', background: ACCENT_GOLD }} />
                    ) : null}
                  </button>
                  {this.state.showMobileFilters ? (
                    <div
                      className="absolute right-0 top-full mt-1 z-50 rounded-xl flex flex-col gap-3 p-3 overflow-y-auto"
                      style={{
                        width: 'min(22rem, calc(100vw - 2rem))',
                        maxHeight: '16rem',
                        background: '#0e0b08',
                        border: `1px solid ${GOLD} 0.25)`,
                        boxShadow: '0 12px 32px rgba(0,0,0,0.75)',
                      }}
                    >
                      <div>
                        <div className="text-xs font-semibold mb-1.5" style={{ color: `${GOLD} 0.55)`, fontFamily: "'FantaisieArtistique', cursive" }}>{t('Sort')}</div>
                        <div className="flex flex-wrap gap-1.5">
                          {(isLib
                            ? [['latest', t('Latest')], ['popular', t('Popular')], ['name', t('Name')], ['trending', t('Trending')]]
                            : [['latest', t('Latest')], ['name', t('Name')], ['oldest', t('Oldest')]]
                          ).map(([key, label]) => pill(
                            (isLib ? this.state.libSort : this.state.myDeckSort) === key,
                            label,
                            () => (isLib
                              ? this.setState({ libSort: key }, reloadLib)
                              : this.setState({ myDeckSort: key })),
                          ))}
                        </div>
                      </div>
                      {isLib ? (
                        <div>
                          <div className="text-xs font-semibold mb-1.5" style={{ color: `${GOLD} 0.55)`, fontFamily: "'FantaisieArtistique', cursive" }}>{t('Show')}</div>
                          <div className="flex flex-wrap gap-1.5">
                            {[['all', t('All')], ['following', t('Friends')], ['liked', t('Liked')]].map(([key, label]) => pill(
                              this.state.libTab === key,
                              label,
                              () => this.setState({ libTab: key }, reloadLib),
                            ))}
                          </div>
                        </div>
                      ) : (
                        <div>
                          <div className="text-xs font-semibold mb-1.5" style={{ color: `${GOLD} 0.55)`, fontFamily: "'FantaisieArtistique', cursive" }}>{t('Type')}</div>
                          <div className="flex flex-wrap gap-1.5">
                            {[['all', t('Any Type')], ['constructed', t('Constructed')], ['peasant', t('Peasant')], ['sandbox', t('Sandbox')]].map(([key, label]) => pill(
                              this.state.myDeckType === key,
                              label,
                              () => this.setState({ myDeckType: key }),
                            ))}
                          </div>
                        </div>
                      )}
                      {!isLib ? (
                        <div>
                          <div className="text-xs font-semibold mb-1.5" style={{ color: `${GOLD} 0.55)`, fontFamily: "'FantaisieArtistique', cursive" }}>{t('Elements')}</div>
                          <div className="flex flex-wrap gap-1.5">
                            {pill(this.state.myDeckElements.size === 0, t('Any'), () => this.setState({ myDeckElements: new Set() }))}
                            {['Water', 'Earth', 'Fire', 'Air'].map((el) => pill(
                              this.state.myDeckElements.has(el),
                              t(el),
                              () => this.setState((s) => {
                                const next = new Set(s.myDeckElements);
                                if (next.has(el)) next.delete(el);
                                else next.add(el);
                                return { myDeckElements: next };
                              }),
                            ))}
                          </div>
                        </div>
                      ) : null}
                      {!isLib && this.getTagOptions().length > 0 ? (
                        <div>
                          <div className="text-xs font-semibold mb-1.5" style={{ color: `${GOLD} 0.55)`, fontFamily: "'FantaisieArtistique', cursive" }}>{t('Tags')}</div>
                          <div className="flex flex-wrap gap-1.5">
                            {[['all', t('Any Tag')], ['__none__', t('Untagged')], ...this.getTagOptions().map((opt) => [opt.name, opt.name])].map(([key, label]) => pill(
                              this.state.myDeckTag === key,
                              label,
                              () => this.setState({ myDeckTag: key }),
                            ))}
                          </div>
                        </div>
                      ) : null}
                      {avatarOptions.length > 1 ? (
                        <div>
                          <div className="text-xs font-semibold mb-1.5" style={{ color: `${GOLD} 0.55)`, fontFamily: "'FantaisieArtistique', cursive" }}>{t('Avatar')}</div>
                          <div className="flex flex-wrap gap-1.5">
                            {pill(!(isLib ? this.state.libAvatar : this.state.myDeckAvatar), t('Any Avatar'), () => (isLib
                              ? this.setState({ libAvatar: null }, reloadLib)
                              : this.setState({ myDeckAvatar: null })))}
                            {avatarOptions.map((o) => pill(
                              (isLib ? this.state.libAvatar : this.state.myDeckAvatar) === o.value,
                              o.label,
                              () => (isLib
                                ? this.setState({ libAvatar: o.value }, reloadLib)
                                : this.setState({ myDeckAvatar: o.value })),
                            ))}
                          </div>
                        </div>
                      ) : null}
                      <button
                        type="button"
                        className="self-end px-4 rounded-lg text-xs font-semibold"
                        style={{ minHeight: '2.25rem', background: `${GOLD} 0.14)`, border: `1px solid ${GOLD} 0.4)`, color: ACCENT_GOLD }}
                        onClick={() => this.setState({ showMobileFilters: false })}
                      >
                        {t('Done')}
                      </button>
                    </div>
                  ) : null}
                </div>
                {this.state.galleryTab === 'my-decks' ? (
                  <button
                    type="button"
                    aria-label={this.state.editMode ? t('Finish editing') : t('Edit deck names and artwork')}
                    className={`gallery-edit-btn flex items-center justify-center shrink-0 rounded-lg${this.state.editMode ? ' is-on' : ''}`}
                    style={{ width: '2.75rem', height: '2.75rem' }}
                    data-sound={UI.CONFIRM}
                    onClick={this.toggleEditMode}
                  >
                    <svg style={{ width: '1.25rem', height: '1.25rem' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 20h9" />
                      <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
                    </svg>
                  </button>
                ) : null}
                <div className="flex items-center flex-1 min-w-0 rounded-lg" style={{ background: '#0e0b08', border: `1px solid ${GOLD} 0.18)` }}>
                  <div className="px-2.5 shrink-0" style={{ color: TEXT_MUTED }}>
                    <svg style={{ width: '1rem', height: '1rem' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                    </svg>
                  </div>
                  <input
                    type="text"
                    placeholder={t('Search decks...')}
                    value={isLib ? this.state.libSearch : this.state.searchQuery}
                    onInput={(e) => {
                      if (isLib) {
                        this.setState({ libSearch: e.target.value });
                        clearTimeout(this._libSearchTimer);
                        this._libSearchTimer = setTimeout(reloadLib, 300);
                      } else {
                        this.setState({ searchQuery: e.target.value });
                      }
                    }}
                    className="flex-1 min-w-0 px-1 py-2 text-sm outline-none"
                    style={{ background: 'transparent', color: TEXT_BODY, border: 'none' }}
                  />
                </div>
              </>
            );
          })()}

          {/* Edit mode. One toggle for the whole collection. While on,
              tiles stop opening the builder and expose their name field
              plus the cover / fanned-card art targets. */}
          {!isPhoneLayout() && this.state.galleryTab === 'my-decks' && (
            <button
              type="button"
              className={`gallery-edit-btn flex items-center gap-2 shrink-0 px-3 py-2 text-xs rounded-lg${this.state.editMode ? ' is-on' : ''}`}
              title={this.state.editMode ? t('Finish editing') : t('Edit deck names and artwork')}
              data-sound={UI.CONFIRM}
              onClick={this.toggleEditMode}
            >
              <svg style={{ width: '0.875cqw', height: '0.875cqw' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 20h9" />
                <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
              </svg>
              {/* Both labels share one grid cell so the button is as wide
                  as the longer word and never resizes on toggle. */}
              <span className="grid">
                <span style={{ gridArea: '1 / 1', visibility: this.state.editMode ? 'hidden' : 'visible' }}>{t('Edit')}</span>
                <span style={{ gridArea: '1 / 1', visibility: this.state.editMode ? 'visible' : 'hidden' }}>{t('Done')}</span>
              </span>
            </button>
          )}

          {/* Library controls. Same capsule + popover as My Decks, wired to
              the library fetch instead of the local filter pass. Search is
              debounced; the other fields reload on change. Elements and
              tags stay off: the library returns a page, not the corpus. */}
          {!isPhoneLayout() && this.state.galleryTab === 'library' && (() => {
            const { libSearch, libSort, libTab, libAvatar, libType } = this.state;
            const reloadLib = () => this.deckLibraryRef?.loadDecks();
            const LIB_TABS = [{ key: 'all', label: k('All') }, { key: 'following', label: k('Friends') }, { key: 'liked', label: k('Liked') }];
            return (
              <>
                <DeckAdvancedFilters
                  title={t('Public Filters')}
                  searchQuery={libSearch}
                  onSearchChange={(v) => {
                    this.setState({ libSearch: v });
                    clearTimeout(this._libSearchTimer);
                    this._libSearchTimer = setTimeout(reloadLib, 300);
                  }}
                  sortOptions={[
                    { value: 'latest', label: k('Latest') },
                    { value: 'popular', label: k('Popular') },
                    { value: 'name', label: k('Name') },
                    { value: 'trending', label: k('Trending') },
                  ]}
                  sort={libSort}
                  onSortChange={(v) => this.setState({ libSort: v }, reloadLib)}
                  type={libType}
                  onTypeChange={(v) => this.setState({ libType: v }, reloadLib)}
                  avatar={libAvatar}
                  onAvatarChange={(v) => this.setState({ libAvatar: v }, reloadLib)}
                  avatarOptions={this.getAvatarOptions()}
                  hideElements
                  hideTag
                  tag="all"
                  elements={EMPTY_SET}
                />
                {/* Sub-tabs */}
                <TabBar className="flex items-center shrink-0 rounded-lg" style={{ background: '#0e0b08', border: `1px solid ${GOLD} 0.18)`, padding: '0.125cqw' }}
                  activeIndex={LIB_TABS.findIndex((tab) => tab.key === libTab)}>
                  {LIB_TABS.map((tab) => (
                    <button key={tab.key} type="button" data-tab-item className="px-3 py-1.5 text-[0.625cqw] font-semibold"
                      style={libTab === tab.key ? TAB_ACTIVE_TEXT : TAB_INACTIVE}
                      onClick={() => this.setState({ libTab: tab.key }, () => this.deckLibraryRef?.loadDecks())}>
                      {t(tab.label)}
                    </button>
                  ))}
                </TabBar>
              </>
            );
          })()}

          {!isPhoneLayout() && this.state.galleryTab === 'my-decks' && (
            <span className="text-xs ml-auto shrink-0" style={{ color: TEXT_MUTED }}>
              {filteredDecks.length === 1 ? t('1 deck') : t('{n} decks', { n: filteredDecks.length })}
            </span>
          )}
        </div>
        </div>

        {/* Body. Library always mounted so ref is available for inline controls */}
        <div className="relative flex-1 min-h-0" style={{ display: this.state.galleryTab === 'library' ? 'flex' : 'none', flexDirection: 'column' }}>
          <div className="h-full">
            <DeckLibrary
              ref={(ref) => { this.deckLibraryRef = ref; }}
              onBodyScroll={this.handleBodyScroll}
              sorceryCards={sorceryCards}
              hideFilterBar
              filterSearch={this.state.libSearch}
              filterSort={this.state.libSort}
              filterTab={this.state.libTab}
              filterAvatar={this.state.libAvatar}
              filterType={this.state.libType}
              onOpenDeck={onOpenLibraryDeck}
              onDeckSaved={() => { if (this.props.onDeckSaved) this.props.onDeckSaved(); }}
            />
          </div>
        </div>
        {this.state.galleryTab === 'my-decks' ? (
        <div
          className="relative flex-1 overflow-y-auto"
          onScroll={(e) => {
            this.handleBodyScroll(e);
            if (this.state.activeSlot) this.setState({ activeSlot: null });
          }}
        >
          <div
            className={isPhoneLayout() ? '' : 'px-6 py-4'}
            style={isPhoneLayout() ? {
              // Constant top padding = overlay chrome height. Never
              // changes with the chrome's visibility, so the scroller
              // geometry stays stable while the chrome slides.
              paddingTop: 'calc(7.25rem + env(safe-area-inset-top, 0px))',
              paddingLeft: 'max(0.75rem, env(safe-area-inset-left, 0px))',
              paddingRight: 'max(0.75rem, env(safe-area-inset-right, 0px))',
              paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom, 0px))',
            } : undefined}
          >

            {/* Grid */}
            <div className={isPhoneLayout() ? 'grid gap-3' : 'grid gap-5'} style={{ gridTemplateColumns: isPhoneLayout() ? 'repeat(3, minmax(0, 1fr))' : 'repeat(auto-fill, minmax(22cqw, 1fr))' }}>

              {/* New deck. A heraldic "empty plaque" that matches the
                  3D framing of the actual deck tiles instead of reading
                  as a web-style dashed placeholder. Layered shadows +
                  textured stone backdrop + Borre-knot corners give it
                  the same volumetric weight as a regular tile. */}
              <button
                type="button"
                data-tutorial="forge-new-deck"
                className="group relative overflow-hidden flex flex-col items-center justify-center transition-all duration-200 hover:scale-[1.015]"
                style={{
                  aspectRatio: '1.5 / 1',
                  background: `${CONTENT_BG_DEFAULT}, linear-gradient(180deg, #14100a 0%, #0a0805 100%)`,
                  backgroundBlendMode: 'overlay, normal',
                  border: `1px solid ${GOLD} 0.55)`,
                  borderRadius: 'var(--radius-lg)',
                  boxShadow: [
                    '0 22px 52px rgba(0,0,0,0.85)',
                    '0 8px 18px rgba(0,0,0,0.55)',
                    '0 2px 4px rgba(0,0,0,0.6)',
                    `0 0 32px ${GOLD} 0.14)`,
                    `inset 0 1px 0 rgba(255,232,170,0.22)`,
                    `inset 0 -1px 0 rgba(0,0,0,0.75)`,
                    `inset 0 0 0 1px ${GOLD} 0.25)`,
                    'inset 0 -22px 44px rgba(0,0,0,0.55)',
                    'inset 0 18px 40px rgba(0,0,0,0.32)',
                  ].join(', '),
                  isolation: 'isolate',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.boxShadow = e.currentTarget.style.boxShadow.replace(`0 0 32px ${GOLD} 0.14)`, `0 0 42px ${GOLD} 0.28)`); }}
                onMouseLeave={(e) => { e.currentTarget.style.boxShadow = e.currentTarget.style.boxShadow.replace(`0 0 42px ${GOLD} 0.28)`, `0 0 32px ${GOLD} 0.14)`); }}
                data-sound={UI.OPEN}
                onClick={() => this.setState({ showCreate: true })}
              >
                {/* Faint sigil sitting behind the icon stack, evoking
                    a stamped or carved insignia waiting to be claimed. */}
                <div
                  aria-hidden
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background: `radial-gradient(circle at 50% 45%, ${GOLD} 0.08) 0%, transparent 55%)`,
                  }}
                />

                <FourCorners color={`${GOLD} 0.7)`} knots knotSize={22} knotInset={4} knotColor={`${GOLD} 0.55)`} knotOpacity={0.55} />

                {/* Heraldic plus. Chiseled cross with a soft gold glow
                    behind it. Larger and weightier than a thin line "+." */}
                <div
                  className="relative flex items-center justify-center transition-transform duration-300 ease-out group-hover:scale-110"
                  style={{
                    width: '3.2cqw',
                    height: '3.2cqw',
                    marginBottom: '0.6cqw',
                  }}
                >
                  <div
                    className="absolute inset-0 rounded-full"
                    style={{
                      background: `radial-gradient(circle, ${GOLD} 0.22) 0%, transparent 65%)`,
                      filter: 'blur(2px)',
                    }}
                  />
                  <svg
                    viewBox="0 0 24 24"
                    className="relative"
                    style={{
                      width: '2.3cqw',
                      height: '2.3cqw',
                      color: ACCENT_GOLD,
                      filter: `drop-shadow(0 1px 0 rgba(0,0,0,0.85)) drop-shadow(0 0 6px ${GOLD} 0.45))`,
                    }}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.4"
                    strokeLinecap="round"
                  >
                    {/* Cross with subtle serif caps. Reads more
                        heraldic than a plain math plus. */}
                    <path d="M12 5v14" />
                    <path d="M5 12h14" />
                    <path d="M10.5 5h3" />
                    <path d="M10.5 19h3" />
                    <path d="M5 10.5v3" />
                    <path d="M19 10.5v3" />
                  </svg>
                </div>

                <div
                  className="arena-heading"
                  style={{
                    fontSize: '0.78cqw',
                    color: ACCENT_GOLD,
                    textShadow: '0 2px 4px rgba(0,0,0,0.9)',
                    opacity: 0.92,
                  }}
                >
                  {t('Forge New Deck')}
                </div>

                <div
                  className="mt-[0.35cqw]"
                  style={{
                    width: '4.5cqw',
                    height: '1px',
                    background: `linear-gradient(90deg, transparent 0%, ${GOLD} 0.45) 50%, transparent 100%)`,
                  }}
                />
              </button>

              {/* Deck cards */}
              {filteredDecks.map((deck, index) => {
                const isConfirming = confirmDeleteId === deck.id;
                const collection = this.props.profile?.collection || [];
                const ownedMap = buildOwnedMap(collection);
                const { count: missingCount } = getMissingCards(deck.cards || [], ownedMap);
                const deckMode = deck.gameMode || 'constructed';
                const isSandbox = deckMode === 'sandbox';

                const modePill = <DeckModePill mode={deckMode} />;

                const editMode = this.state.editMode;

                const topRight = (
                  <>
                    {isPhoneLayout() ? modePill : null}
                    {/* Delete sits far left, away from the anchored bookmark,
                        so the destructive action never neighbors the one
                        button that stays visible after hover ends. */}
                    <button
                      type="button"
                      className="deck-tile-icon-btn deck-tile-icon-btn-danger flex items-center justify-center rounded-full opacity-0 group-hover:opacity-100"
                      style={{ width: '1.5cqw', height: '1.5cqw' }}
                      title={t('Delete deck')}
                      data-sound={UI.CANCEL}
                      onClick={(e) => { e.stopPropagation(); this.setState({ confirmDeleteId: deck.id }); }}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" style={{ width: '0.85cqw', height: '0.85cqw' }}>
                        <path d="m6 6 12 12" />
                        <path d="m18 6-12 12" />
                      </svg>
                    </button>
                    {onDuplicateDeck ? (
                      <button
                        type="button"
                        className="deck-tile-icon-btn deck-tile-icon-btn-gold flex items-center justify-center rounded-full opacity-0 group-hover:opacity-100"
                        style={{ width: '1.5cqw', height: '1.5cqw' }}
                        title={t('Duplicate deck')}
                        data-sound={UI.CONFIRM}
                        onClick={(e) => { e.stopPropagation(); onDuplicateDeck(deck.id); }}
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '0.8cqw', height: '0.8cqw' }}>
                          <rect x="9" y="9" width="13" height="13" rx="2" />
                          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                        </svg>
                      </button>
                    ) : null}
                    {onToggleShare ? (
                      <button
                        type="button"
                        className="deck-tile-icon-btn deck-tile-icon-btn-gold flex items-center justify-center rounded-full opacity-0 group-hover:opacity-100"
                        style={{ width: '1.5cqw', height: '1.5cqw' }}
                        title={deck.shared ? t('Unshare from Deck Library') : t('Share to Deck Library')}
                        data-sound={UI.CONFIRM}
                        onClick={(e) => { e.stopPropagation(); onToggleShare(deck); }}
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '0.8cqw', height: '0.8cqw' }}>
                          <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
                          <polyline points="16 6 12 2 8 6" />
                          <line x1="12" y1="2" x2="12" y2="15" />
                        </svg>
                      </button>
                    ) : null}
                    {onToggleFavorite ? (
                      <button
                        type="button"
                        // Last in the row so it renders flush in the tile's
                        // top-right corner. A favorited bookmark stays visible
                        // after hover ends, so it has to rest at an anchored
                        // spot, not mid-row where the hidden buttons live.
                        className={`deck-tile-icon-btn deck-tile-icon-btn-gold flex items-center justify-center rounded-full${deck.favorite ? '' : ' opacity-0 group-hover:opacity-100'}`}
                        style={{ width: '1.5cqw', height: '1.5cqw' }}
                        title={deck.favorite ? t('Unfavorite deck') : t('Favorite deck. Keeps it at the top')}
                        data-sound={UI.CONFIRM}
                        onClick={(e) => { e.stopPropagation(); onToggleFavorite(deck); }}
                      >
                        <svg viewBox="0 0 24 24" fill={deck.favorite ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '0.85cqw', height: '0.85cqw', color: deck.favorite ? ACCENT_GOLD : undefined }}>
                          <path d="M19 21l-7-4-7 4V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
                        </svg>
                      </button>
                    ) : null}
                  </>
                );

                const bottomLeftSlot = deck.shared ? (
                  <div className="flex items-center justify-center rounded-full" style={{ width: '1.4cqw', height: '1.4cqw', background: 'rgba(0,0,0,0.7)', border: `1px solid ${GOLD} 0.35)` }} title={t('Shared to Deck Library')}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '0.75cqw', height: '0.75cqw', color: ACCENT_GOLD }}>
                      <circle cx="18" cy="5" r="3" />
                      <circle cx="6" cy="12" r="3" />
                      <circle cx="18" cy="19" r="3" />
                      <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                      <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                    </svg>
                  </div>
                ) : null;

                const footerLeft = (
                  <>
                    {editMode ? (
                      <input
                        type="text"
                        defaultValue={deck.name}
                        maxLength={100}
                        className="arena-heading font-bold w-full px-1.5 py-0.5 outline-none"
                        style={{
                          fontSize: '0.82cqw',
                          lineHeight: 1.15,
                          color: TEXT_PRIMARY,
                          background: 'rgba(8,6,4,0.92)',
                          border: `1px solid ${GOLD} 0.55)`,
                          borderRadius: 'var(--radius-sm)',
                        }}
                        onClick={(e) => e.stopPropagation()}
                        onBlur={(e) => this.commitDeckName(deck, e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') e.target.blur();
                          if (e.key === 'Escape') { e.stopPropagation(); e.target.value = deck.name; e.target.blur(); }
                        }}
                      />
                    ) : DeckTileText.title(deck.name)}
                    <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                      {deck.savedAt ? <span style={{ color: TEXT_MUTED, fontSize: TXT(0.58, 0.75) }}>{timeAgo(deck.savedAt)}</span> : null}
                    </div>
                    {/* Bottom line: card count and the mode pill. The
                        pill stays on the tile's last line (its original
                        height) instead of riding up with the date. */}
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <span className="tabular-nums" style={{ color: TEXT_MUTED, fontSize: TXT(0.6, 0.75) }}>{(deck.cardCount ?? 0) === 1 ? t('1 card') : t('{n} cards', { n: deck.cardCount ?? 0 })}</span>
                      {isPhoneLayout() ? null : modePill}
                      {missingCount > 0 && !isSandbox && (
                        <span className="px-1.5 py-0.5 rounded-full" style={{ fontSize: TXT(0.5, 0.625), background: 'rgba(180,60,60,0.2)', color: '#e89090', border: '1px solid rgba(180,60,60,0.3)' }}>
                          {missingCount === 1 ? t('1 missing') : t('{n} missing', { n: missingCount })}
                        </span>
                      )}
                    </div>
                  </>
                );

                const footerRight = (
                  <button
                    type="button"
                    // Hover-revealed like the tile's other actions, but
                    // pinned visible while its menu is open so the
                    // anchor doesn't vanish under the popover.
                    className={`deck-tile-icon-btn deck-tile-icon-btn-gold deck-tag-btn flex items-center justify-center rounded-full${this.state.tagMenu?.deckId === deck.id ? '' : ' opacity-0 group-hover:opacity-100'}`}
                    // The footer band pads 0.7cqw bottom but 0.95cqw
                    // sides; compensate so the button's corner gap is
                    // even on both axes.
                    style={{ width: '1.5cqw', height: '1.5cqw', marginBottom: '0.25cqw' }}
                    title={(deck.tags || []).length > 0 ? t('Tags: {tags}', { tags: deck.tags.join(', ') }) : t('Add tags')}
                    data-sound={UI.CONFIRM}
                    onClick={(e) => {
                      e.stopPropagation();
                      this.setState((s) => ({
                        tagMenu: s.tagMenu?.deckId === deck.id
                          ? null
                          : { deckId: deck.id, anchor: e.currentTarget.getBoundingClientRect() },
                      }));
                    }}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '0.8cqw', height: '0.8cqw' }}>
                      {TAG_GLYPH}
                    </svg>
                  </button>
                );

                const overlay = isConfirming ? (
                  <div
                    className="absolute inset-0 flex flex-col items-center justify-center gap-[0.35cqw] p-3"
                    style={{
                      background: `${CONTENT_BG_DEFAULT}, linear-gradient(180deg, #14100a 0%, #060403 100%)`,
                      backgroundBlendMode: 'overlay, normal',
                      borderRadius: 'var(--radius-lg)',
                      boxShadow: [
                        `inset 0 0 0 1px ${GOLD} 0.45)`,
                        'inset 0 0 36px rgba(0,0,0,0.75)',
                        `0 0 24px ${GOLD} 0.15)`,
                      ].join(', '),
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {/* Hugs the inner inset ring, not the panel edge: explicit
                        radius = parent 0.6cqw minus the 2dpx inset. */}
                    <FourCorners color={`${GOLD} 0.6)`} radius={8} inset={2} />

                    <span
                      className="arena-heading text-center"
                      style={{
                        color: TEXT_PRIMARY,
                        fontSize: '0.95cqw',
                        textShadow: '0 2px 4px rgba(0,0,0,0.95)',
                      }}
                    >
                      {t('Delete this deck?')}
                    </span>

                    <span
                      className="text-center italic px-2"
                      style={{
                        color: ACCENT_GOLD,
                        fontSize: '0.65cqw',
                        opacity: 0.9,
                        lineHeight: 1.2,
                      }}
                    >
                      &ldquo;{deck.name}&rdquo;
                    </span>

                    <div
                      style={{
                        width: '55%',
                        height: '1px',
                        background: `linear-gradient(90deg, transparent 0%, ${GOLD} 0.5) 50%, transparent 100%)`,
                        marginTop: '0.15cqw',
                        marginBottom: '0.15cqw',
                      }}
                    />

                    <div className="flex gap-[0.55cqw]">
                      <button
                        type="button"
                        className="deck-confirm-btn deck-confirm-btn-danger arena-heading"
                        style={{ fontSize: TXT(0.62, 0.6875) }}
                        data-sound={UI.CANCEL}
                        onClick={(e) => { e.stopPropagation(); onDeleteDeck(deck.id); this.setState({ confirmDeleteId: null }); }}
                      >
                        {t('Delete')}
                      </button>
                      <button
                        type="button"
                        className="deck-confirm-btn deck-confirm-btn-cancel arena-heading"
                        style={{ fontSize: TXT(0.62, 0.6875) }}
                        data-sound={UI.CANCEL}
                        onClick={(e) => { e.stopPropagation(); this.setState({ confirmDeleteId: null }); }}
                      >
                        {t('Keep')}
                      </button>
                    </div>
                  </div>
                ) : null;

                const openSlot = this.state.activeSlot?.deckId === deck.id ? this.state.activeSlot.slot : null;

                return (
                  <DeckTile
                    key={deck.id}
                    deck={editMode ? { ...deck, showcase: this.showcaseFor(deck) } : deck}
                    sorceryCards={sorceryCards}
                    onClick={() => onOpenDeck(deck.id)}
                    topRightSlot={topRight}
                    bottomLeftSlot={bottomLeftSlot}
                    footerLeftSlot={footerLeft}
                    footerRightSlot={footerRight}
                    overlay={overlay}
                    editingShowcase={editMode}
                    activeSlot={openSlot}
                    enterIndex={index}
                    onSlotClick={(slot, anchor) => this.setState((s) => ({
                      // Clicking the open slot again closes the picker.
                      activeSlot: s.activeSlot?.deckId === deck.id && s.activeSlot.slot === slot
                        ? null
                        : { deckId: deck.id, slot, anchor },
                    }))}
                  />
                );
              })}
            </div>

            {/* Empty state */}
            {filteredDecks.length === 0 && searchQuery.trim() ? (
              <div className="flex flex-col items-center justify-center mt-16">
                <span className="text-sm" style={{ color: TEXT_MUTED }}>{t('No decks match "{query}"', { query: searchQuery })}</span>
              </div>
            ) : null}

            {filteredDecks.length === 0 && !searchQuery.trim() ? (
              <div className="flex flex-col items-center justify-center mt-12">
                <span className="text-sm" style={{ color: TEXT_MUTED }}>{t('No decks yet. Create your first deck to get started.')}</span>
              </div>
            ) : null}
          </div>
        </div>
        ) : null}
        {/* Slot picker. One instance for the whole grid, positioned
            against the canvas so it can never be clipped by a tile
            near an edge. */}
        {(() => {
          const slot = this.state.activeSlot;
          const deck = slot ? (this.props.savedDecks || []).find((d) => d.id === slot.deckId) : null;
          if (!deck) return null;
          return (
            <DeckShowcasePicker
              deck={deck}
              sorceryCards={sorceryCards}
              anchor={slot.anchor}
              onPick={this.pickShowcaseCard}
              onReset={() => this.pickShowcaseCard(null)}
              onClose={() => this.setState({ activeSlot: null })}
            />
          );
        })()}
        {this.state.showCreate && (
          <CreateDeckModal
            sorceryCards={sorceryCards}
            savedDecks={this.props.savedDecks}
            profile={this.props.profile}
            onClose={() => this.setState({ showCreate: false })}
            onCreate={(config) => {
              this.setState({ showCreate: false });
              onCreateDeck(config);
            }}
          />
        )}
        {/* Tag menu. One instance for the whole grid, positioned
            against the canvas so it can never be clipped by a tile
            near an edge (same pattern as the showcase slot picker). */}
        {this.state.tagMenu && (() => {
          const deck = (this.props.savedDecks || []).find((d) => d.id === this.state.tagMenu.deckId);
          if (!deck) return null;
          return (
            <DeckTagMenu
              deck={deck}
              allTags={this.getTagOptions().map((t) => t.name)}
              anchor={this.state.tagMenu.anchor}
              onClose={() => this.setState({ tagMenu: null })}
              onChange={(tags) => this.props.onSetDeckTags?.(deck.id, tags)}
            />
          );
        })()}
        {this.state.showManageTags && (
          <ManageTagsModal
            tags={this.getTagOptions()}
            onClose={() => this.setState({ showManageTags: false })}
            onDeleteTag={(tag) => {
              // A filter pinned to the deleted tag would show an empty
              // grid with no way to see why; reset it alongside.
              if (this.state.myDeckTag === tag) this.setState({ myDeckTag: 'all' });
              this.props.onDeleteTag?.(tag);
            }}
          />
        )}
        {this.state.showImport && (
          <DeckImportModal
            sorceryCards={sorceryCards}
            onClose={() => this.setState({ showImport: false })}
            onImport={(data) => {
              this.setState({ showImport: false });
              if (this.props.onImportDeck) this.props.onImportDeck(data);
            }}
          />
        )}
        {this.state.showGalleryTutorial && (
          <TutorialOverlay
            steps={GALLERY_TUTORIAL_STEPS}
            onDismiss={this.handleGalleryTutorialDismiss}
          />
        )}
      </div>
    );
  }
}
